import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getMe } from "../features/authSlice";

import {
  FaArrowLeft,
  FaUpload,
  FaLeaf,
  FaUser,
  FaFileAlt,
  FaImage,
  FaTrash,
  FaWeightHanging,
  FaTag,
} from "react-icons/fa";

import { useStateContext } from "../contexts/ContextProvider";

const EditProduk = () => {
  const [nama, setNama] = useState("");
  const [deskripsi, setDeskripsi] = useState("");
  const [unit, setUnit] = useState("");
  const [harga, setHarga] = useState("");
  const [hargaDisplay, setHargaDisplay] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef(null);
  const { currentColor, currentMode } = useStateContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { id } = useParams();

  // Colors
  const isDark = currentMode === "Dark";
  const accentColor = currentColor || "#c9862a";
  const bgColor = isDark ? "#1a1a18" : "#f7f3ec";
  const cardBg = isDark ? "#242420" : "#ffffff";
  const borderColor = isDark ? "#2e2e28" : "#e8e0d0";
  const textPrimary = isDark ? "#f0ece3" : "#1a1a18";
  const textSecondary = isDark ? "#a09880" : "#6b6456";
  const inputBg = isDark ? "#1a1a18" : "#ffffff";
  const inputBorder = isDark ? "#3a3a34" : "#e8e0d0";

  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      getProdukById();
    } else {
      navigate("/");
    }
  }, [navigate, user]);

  const getProdukById = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      const response = await axios.get(`${apiUrl}/produk/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;
      setNama(data.nama || "");
      setDeskripsi(data.deskripsi || "");
      setUnit(data.unit || "");
      setPreview(data.url || null);

      const hargaRaw = data.harga ? data.harga.toString() : "";
      setHarga(hargaRaw);
      setHargaDisplay(
        hargaRaw
          ? new Intl.NumberFormat("id-ID").format(parseInt(hargaRaw, 10))
          : "",
      );
    } catch (error) {
      console.error("Error fetching team data:", error);
    }
  };

  // ── Harga formatting helpers ─────────────────────────────────────────────
  const formatRupiah = (value) => {
    if (!value) return "";
    const number = value.toString().replace(/\D/g, "");
    if (!number) return "";
    return new Intl.NumberFormat("id-ID").format(parseInt(number, 10));
  };

  const handleHargaChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    setHarga(raw);
    setHargaDisplay(raw ? formatRupiah(raw) : "");
  };

  // ── Validation ───────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!nama.trim()) newErrors.nama = "Nama produk wajib diisi";
    if (!unit || parseInt(unit, 10) <= 0)
      newErrors.unit = "Unit wajib diisi dan harus lebih dari 0";
    if (!harga || parseInt(harga, 10) <= 0)
      newErrors.harga = "Harga wajib diisi dan harus lebih dari 0";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateProduk = async (e) => {
    e.preventDefault();
    setError("");

    // Validate
    if (!validateForm()) {
      return;
    }

    const formData = new FormData();
    formData.append("nama", nama);
    formData.append("deskripsi", deskripsi);
    formData.append("unit", unit);
    formData.append("harga", harga);
    if (file) {
      formData.append("file", file);
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      await axios.patch(`${apiUrl}/produk/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      navigate("/list-produk");
    } catch (error) {
      console.error("Error updating produk:", error);
      if (error.response?.data?.msg) {
        setError(error.response.data.msg);
      } else {
        setError("Gagal memperbarui data produk");
      }
    }
  };

  // ── File handlers ────────────────────────────────────────────────────────
  const processFile = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);
  };

  const handleFileChange = (e) => processFile(e.target.files[0]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    processFile(e.dataTransfer.files[0]);
  };

  const handleRemoveImage = (e) => {
    e?.stopPropagation();
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUploadClick = (e) => {
    e?.stopPropagation();
    fileInputRef.current?.click();
  };

  // ── Shared input style helper ────────────────────────────────────────────
  const inputStyle = (hasError) => ({
    backgroundColor: inputBg,
    borderColor: hasError ? "#ef4444" : inputBorder,
    color: textPrimary,
  });

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
      {/* Background Pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-5">
          <FaLeaf
            className="absolute top-20 left-10 text-8xl"
            style={{ color: accentColor }}
          />
          <FaImage
            className="absolute bottom-20 right-20 text-7xl"
            style={{ color: accentColor }}
          />
        </div>
      </div>

      <div className="relative z-10 mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/list-produk")}
            className="flex items-center gap-2 mb-4 transition-all duration-300 hover:opacity-80"
            style={{ color: textSecondary }}
          >
            <FaArrowLeft className="text-sm" />
            <span className="text-sm font-medium">
              Kembali ke Daftar Produk
            </span>
          </button>

          <div
            className="p-6 rounded-xl border"
            style={{ backgroundColor: cardBg, borderColor }}
          >
            <div className="flex items-center gap-4">
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: accentColor }}
              >
                <FaUser className="text-white text-xl" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="font-serif text-xs font-bold uppercase tracking-wider"
                    style={{ color: textSecondary }}
                  >
                    Produk Management
                  </span>
                  <span className="text-xs" style={{ color: textSecondary }}>
                    —
                  </span>
                  <span
                    className="font-serif text-xs font-bold"
                    style={{ color: accentColor }}
                  >
                    Rumoh Pangan Aceh
                  </span>
                </div>
                <h1
                  className="text-2xl font-serif font-bold"
                  style={{ color: textPrimary }}
                >
                  Edit Produk Baru
                </h1>
                <p className="text-sm mt-1" style={{ color: textSecondary }}>
                  Edit produk untuk ditampilkan di website
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={updateProduk}>
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left – Image Upload */}
            <div className="lg:col-span-1">
              <div
                className="p-6 rounded-xl border sticky top-6"
                style={{ backgroundColor: cardBg, borderColor }}
              >
                <h3
                  className="font-semibold mb-4 flex items-center gap-2"
                  style={{ color: textPrimary }}
                >
                  <FaImage className="text-sm" style={{ color: accentColor }} />
                  Gambar
                </h3>

                <div
                  className="relative rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden cursor-pointer"
                  style={{
                    borderColor: isDragging ? accentColor : inputBorder,
                    backgroundColor: isDragging ? `${accentColor}15` : inputBg,
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleUploadClick}
                >
                  {preview ? (
                    <div className="relative">
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-full h-64 object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                        <button
                          type="button"
                          onClick={handleUploadClick}
                          className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                        >
                          <FaUpload className="text-white text-sm" />
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="p-2 rounded-lg bg-red-500/80 hover:bg-red-500 transition-colors"
                        >
                          <FaTrash className="text-white text-sm" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 px-4">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                        style={{ backgroundColor: `${accentColor}20` }}
                      >
                        <FaUpload
                          className="text-2xl"
                          style={{ color: accentColor }}
                        />
                      </div>
                      <p
                        className="text-sm font-medium text-center mb-1"
                        style={{ color: textPrimary }}
                      >
                        Drag & Drop gambar di sini
                      </p>
                      <p
                        className="text-xs text-center"
                        style={{ color: textSecondary }}
                      >
                        atau klik untuk memilih file
                      </p>
                      <p
                        className="text-[10px] mt-3 px-3 py-1 rounded-full"
                        style={{
                          color: textSecondary,
                          backgroundColor: `${accentColor}15`,
                        }}
                      >
                        Format: JPG, PNG (Maks. 2MB)
                      </p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* Right – Form Fields */}
            <div className="lg:col-span-2 space-y-6">
              <div
                className="p-6 rounded-xl border"
                style={{ backgroundColor: cardBg, borderColor }}
              >
                <h3
                  className="font-semibold mb-6 flex items-center gap-2"
                  style={{ color: textPrimary }}
                >
                  <FaUser className="text-sm" style={{ color: accentColor }} />
                  Informasi Dasar
                </h3>

                {/* Nama */}
                <div className="mb-5">
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: textPrimary }}
                  >
                    Nama Produk <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaUser
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-sm"
                      style={{ color: textSecondary }}
                    />
                    <input
                      type="text"
                      value={nama}
                      onChange={(e) => setNama(e.target.value)}
                      placeholder="Masukkan nama produk"
                      className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none"
                      style={inputStyle(errors.nama)}
                      onFocus={(e) =>
                        (e.target.style.borderColor = accentColor)
                      }
                      onBlur={(e) =>
                        (e.target.style.borderColor = errors.nama
                          ? "#ef4444"
                          : inputBorder)
                      }
                    />
                  </div>
                  {errors.nama && (
                    <p className="text-xs mt-1" style={{ color: "#ef4444" }}>
                      {errors.nama}
                    </p>
                  )}
                </div>

                {/* Deskripsi */}
                <div className="mb-5">
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: textPrimary }}
                  >
                    Deskripsi
                  </label>
                  <div className="relative">
                    <FaFileAlt
                      className="absolute left-4 top-4 text-sm"
                      style={{ color: textSecondary }}
                    />
                    <textarea
                      value={deskripsi}
                      onChange={(e) => setDeskripsi(e.target.value)}
                      placeholder="Tuliskan deskripsi singkat tentang produk"
                      rows={5}
                      className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none resize-none"
                      style={inputStyle(errors.deskripsi)}
                      onFocus={(e) =>
                        (e.target.style.borderColor = accentColor)
                      }
                      onBlur={(e) =>
                        (e.target.style.borderColor = errors.deskripsi
                          ? "#ef4444"
                          : inputBorder)
                      }
                    />
                  </div>
                  <div className="flex justify-between mt-2">
                    {errors.deskripsi && (
                      <p className="text-xs" style={{ color: "#ef4444" }}>
                        {errors.deskripsi}
                      </p>
                    )}
                    <p
                      className={`text-xs ml-auto ${deskripsi.length > 500 ? "text-red-500" : ""}`}
                      style={{ color: textSecondary }}
                    >
                      {deskripsi.length}/500 karakter
                    </p>
                  </div>
                </div>

                {/* Unit & Harga — side by side */}
                <div className="grid sm:grid-cols-2 gap-5">
                  {/* Unit */}
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: textPrimary }}
                    >
                      Unit (gram) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FaWeightHanging
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-sm"
                        style={{ color: textSecondary }}
                      />
                      <input
                        type="number"
                        min="1"
                        value={unit}
                        onChange={(e) =>
                          setUnit(e.target.value.replace(/\D/g, ""))
                        }
                        placeholder="Contoh: 250"
                        className="w-full px-4 py-3 pl-11 pr-16 rounded-lg border transition-all duration-300 outline-none"
                        style={inputStyle(errors.unit)}
                        onFocus={(e) =>
                          (e.target.style.borderColor = accentColor)
                        }
                        onBlur={(e) =>
                          (e.target.style.borderColor = errors.unit
                            ? "#ef4444"
                            : inputBorder)
                        }
                      />
                      {/* Suffix badge */}
                      <span
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold px-2 py-0.5 rounded-md"
                        style={{
                          backgroundColor: `${accentColor}15`,
                          color: accentColor,
                        }}
                      >
                        gram
                      </span>
                    </div>
                    {errors.unit && (
                      <p className="text-xs mt-1" style={{ color: "#ef4444" }}>
                        {errors.unit}
                      </p>
                    )}
                  </div>

                  {/* Harga */}
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: textPrimary }}
                    >
                      Harga <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      {/* Prefix badge */}
                      <span
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold px-2 py-0.5 rounded-md select-none"
                        style={{
                          backgroundColor: `${accentColor}15`,
                          color: accentColor,
                        }}
                      >
                        Rp
                      </span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={hargaDisplay}
                        onChange={handleHargaChange}
                        placeholder="0"
                        className="w-full px-4 py-3 pl-14 rounded-lg border transition-all duration-300 outline-none"
                        style={inputStyle(errors.harga)}
                        onFocus={(e) =>
                          (e.target.style.borderColor = accentColor)
                        }
                        onBlur={(e) =>
                          (e.target.style.borderColor = errors.harga
                            ? "#ef4444"
                            : inputBorder)
                        }
                      />
                      <FaTag
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-sm"
                        style={{ color: textSecondary }}
                      />
                    </div>
                    {errors.harga && (
                      <p className="text-xs mt-1" style={{ color: "#ef4444" }}>
                        {errors.harga}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div
                className="p-6 rounded-xl border flex flex-col sm:flex-row gap-4 justify-end"
                style={{ backgroundColor: cardBg, borderColor }}
              >
                <button
                  type="button"
                  onClick={() => navigate("/list-produk")}
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: isDark ? "#1a1a18" : "#f7f3ec",
                    color: textPrimary,
                    border: `1px solid ${borderColor}`,
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = isDark
                      ? "#282C33"
                      : "#e8e0d0")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = isDark
                      ? "#1a1a18"
                      : "#f7f3ec")
                  }
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-medium text-white transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: accentColor }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#b5772a")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = accentColor)
                  }
                >
                  <FaLeaf className="text-sm" />
                  {isSubmitting ? "Menyimpan..." : "Update Produk"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProduk;
